/**
 * Resolve a module path relative to a base file path.
 *
 * Handles:
 *   - Workspace alias (`@/foo/bar`) → rewritten to `/foo/bar` so it's looked
 *     up as an absolute virtual path (matches how the AI pipeline's
 *     TypeCheckTool resolves the same alias)
 *   - Scoped packages (`@gotti/client`) → returned as-is
 *   - Bare packages (`zod`, `typescript`) → returned as-is
 *   - Relative paths (`./foo`, `../shared/bar`) → resolved against baseDir
 *   - `aliasRoot` (default `/`) is where `@/` points: `/src/` for a project tree
 *
 * This is the canonical implementation used by both the browser runtime
 * and the AI backend smoke-test tool.
 */
export function resolveModulePath(basePath: string, importPath: string, aliasRoot = '/'): string {
  // Workspace-root alias: `@/foo` → `<aliasRoot>foo`. The root is `/` for the v1 view the AI pipeline and its
  // TypeCheckTool use (`/shared/…`), and `/src/` for a project tree, whose tsconfig maps `@/*` to `./src/*`.
  // Pipeline TypeCheckTool resolves this identically; runtime must match or imports that typecheck cleanly will fail
  // to load.
  if (importPath.startsWith('@/')) {
    return aliasRoot + importPath.slice(2);
  }

  // Non-relative imports are external packages — return as-is
  if (!importPath.startsWith('.') && !importPath.startsWith('/')) {
    return importPath;
  }

  const baseDir = basePath.split('/').slice(0, -1);
  const importParts = importPath.split('/');

  for (const part of importParts) {
    if (part === '.') continue;
    if (part === '..') {
      if (baseDir.length > 0) baseDir.pop();
    } else {
      baseDir.push(part);
    }
  }

  return baseDir.join('/');
}
